import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, ClipboardList, Gift, CheckCircle2, Store, Building2, Filter } from "lucide-react";

const TABS = [
  { id: "all",         label: "Tous" },
  { id: "with_bon",    label: "Avec bon 🎟" },
  { id: "commercant",  label: "Commerçants" },
  { id: "mairie",      label: "Mairie" },
];

export default function SurveysPage() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    api.get("/surveys")
      .then(({ data }) => setSurveys(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (tab === "with_bon") return surveys.filter((s) => !!s.voucher_label);
    if (tab === "commercant") return surveys.filter((s) => s.creator_role === "commercant");
    if (tab === "mairie") return surveys.filter((s) => s.creator_role === "admin");
    return surveys;
  }, [surveys, tab]);

  const counts = useMemo(() => ({
    all: surveys.length,
    with_bon: surveys.filter((s) => !!s.voucher_label).length,
    commercant: surveys.filter((s) => s.creator_role === "commercant").length,
    mairie: surveys.filter((s) => s.creator_role === "admin").length,
  }), [surveys]);

  return (
    <div className="space-y-8" data-testid="surveys-page">
      <header className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="phenix-eyebrow mb-3">Chapitre · Consulter</div>
          <h1 className="heading-display text-2xl lg:text-3xl text-plum-900">
            Sondages <em className="text-pink-500">en cours.</em>
          </h1>
          <p className="mt-3 text-[14px] text-plum-700/70 max-w-xl leading-relaxed">
            Donnez votre avis et gagnez parfois un bon de réduction chez vos commerçants. 30 secondes suffisent.
          </p>
        </div>
        <div className="text-right">
          <div className="phenix-eyebrow mb-2">Ouverts</div>
          <div className="font-display text-3xl text-plum-900 leading-none">
            {String(surveys.length).padStart(2, "0")}
            <span className="text-pink-500 text-lg font-light ml-1">/ {counts.with_bon} avec bon</span>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="surveys-tabs">
        {TABS.map((t) => {
          const active = tab === t.id;
          const count = counts[t.id] || 0;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`surveys-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-[13px] font-bold border transition-colors whitespace-nowrap ${
                active
                  ? "bg-plum-700 text-white border-plum-700 shadow-md shadow-plum-700/15"
                  : "bg-white text-plum-700 border-cream-200 hover:bg-cream-50"
              }`}
            >
              {t.label}
              <span className={`tabular-nums text-[11px] px-1.5 rounded-full ${active ? "bg-white/20" : "bg-cream-100 text-plum-700/60"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-plum-700/50 italic py-12 text-center">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-cream-200 p-16 text-center text-plum-700/50 italic rounded-2xl">
          Aucun sondage dans cette catégorie pour le moment.
        </div>
      ) : (
        <>
          {/* Table view (md+) */}
          <div className="hidden md:block overflow-x-auto rounded-2xl border border-cream-200 bg-white" data-testid="surveys-table">
            <table className="w-full text-[14px]">
              <thead className="bg-cream-50 text-[10px] uppercase tracking-wider text-plum-700/65 font-bold">
                <tr>
                  <th className="text-left px-4 py-3">Sondage</th>
                  <th className="text-left px-3 py-3">Auteur</th>
                  <th className="text-center px-3 py-3">Questions</th>
                  <th className="text-center px-3 py-3">Récompense</th>
                  <th className="text-center px-3 py-3">Réponses</th>
                  <th className="text-right px-3 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-100">
                {filtered.map((s) => {
                  const isMerchant = s.creator_role === "commercant";
                  return (
                    <tr key={s.id} className={`hover:bg-cream-50/60 ${s.responded ? "opacity-70" : ""}`} data-testid={`survey-row-${s.id}`}>
                      <td className="px-4 py-3.5 align-top">
                        <div className="font-display font-bold text-plum-900 leading-snug">{s.title}</div>
                        <div className="text-[12px] text-plum-700/65 mt-0.5 line-clamp-1">{s.description}</div>
                      </td>
                      <td className="px-3 py-3.5 align-top">
                        <div className="inline-flex items-center gap-1.5 text-[12px]">
                          {isMerchant ? <Store size={12} className="text-sun-600" strokeWidth={2.4} /> : <Building2 size={12} className="text-plum-700" strokeWidth={2.4} />}
                          <span className="font-semibold text-plum-900">{s.creator_name || "—"}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3.5 text-center tabular-nums font-bold text-plum-700 align-top">
                        {s.questions?.length || 0}
                      </td>
                      <td className="px-3 py-3.5 text-center align-top">
                        {s.voucher_label ? (
                          <span className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-pink-50 border border-pink-200 text-pink-600 text-[11px] font-bold">
                            <Gift size={11} strokeWidth={2.5} /> {s.voucher_label}
                          </span>
                        ) : (
                          <span className="text-plum-700/30 text-[12px]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3.5 text-center tabular-nums text-plum-700/65 text-[13px] align-top">
                        {s.response_count || 0}
                      </td>
                      <td className="px-3 py-3.5 text-right align-top">
                        {s.responded ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-mint-600 uppercase tracking-wider" data-testid={`survey-responded-${s.id}`}>
                            <CheckCircle2 size={12} /> Répondu
                          </span>
                        ) : (
                          <Link to={`/sondages/${s.id}`}
                            data-testid={`survey-take-btn-${s.id}`}
                            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-plum-700 hover:bg-plum-800 text-white text-[12px] font-bold transition-colors">
                            Répondre <ArrowUpRight size={12} />
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Card view (mobile) */}
          <div className="md:hidden space-y-3" data-testid="surveys-cards">
            {filtered.map((s) => {
              const isMerchant = s.creator_role === "commercant";
              return (
                <Link key={s.id} to={`/sondages/${s.id}`} data-testid={`survey-take-btn-${s.id}`}
                  className="block phenix-card bg-white p-4 hover:-translate-y-0.5 transition-transform">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="inline-flex items-center gap-1.5 text-[11px]">
                      {isMerchant ? <Store size={11} className="text-sun-600" strokeWidth={2.4} /> : <Building2 size={11} className="text-plum-700" strokeWidth={2.4} />}
                      <span className="font-semibold text-plum-700">{s.creator_name || "—"}</span>
                    </div>
                    {s.responded && (
                      <span className="text-[10px] uppercase tracking-wider text-mint-600 font-bold inline-flex items-center gap-1">
                        <CheckCircle2 size={10} /> Répondu
                      </span>
                    )}
                  </div>
                  <h3 className="font-display font-bold text-[15px] text-plum-900 leading-tight mb-1">{s.title}</h3>
                  <p className="text-[12px] text-plum-700/70 line-clamp-2 mb-2">{s.description}</p>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] text-plum-700/55">
                      {s.questions?.length || 0} question{(s.questions?.length || 0) > 1 ? "s" : ""} · {s.response_count || 0} réponse{(s.response_count || 0) > 1 ? "s" : ""}
                    </span>
                    {s.voucher_label && (
                      <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-pink-50 border border-pink-200 text-pink-600 text-[10px] font-bold">
                        <Gift size={10} strokeWidth={2.5} /> {s.voucher_label}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
